import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    LayoutDashboard,
    Search,
    Clock,
    ShoppingCart,
    FileText,
    FileSignature,
    ClipboardList,
    Truck,
    Undo2,
    Inbox,
    Users,
    Package,
    Settings,
    Mail,
    FileCode,
    Plug,
    RefreshCcw,
    ListChecks,
    AlertTriangle,
    ScrollText,
    LogOut,
    Boxes,
    ChevronLeft,
    ChevronRight,
    Database,
    KeyRound,
    UserPlus,
    Workflow,
    Target,
    Building2,
    Contact,
    ListTodo,
    BarChart3,
    PieChart,
    Send,
    Receipt,
    Bell,
} from "lucide-react";

import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

export const MODULE_ACCESS = {
    "/dashboard": { roles: ["admin", "sales_agent", "branch_manager", "treasury", "accounting", "inquiry_dept", "logistics", "purchasing", "partner", "operations_head", "branch_oic", "cashier"] },

    "/inquiry": { perms: ["inquiry.read"], roles: ["branch_manager", "owner"] },
    "/admin/inquiry-reports": { roles: ["admin", "inquiry_dept", "purchasing", "accounting"] },
    "/non-organic/orders": { perms: ["cart.read"], roles: ["admin", "branch_manager", "inquiry_dept", "purchasing", "owner"] },
    "/cart": { perms: ["cart.read"], roles: ["branch_manager"] },
    "/project-registrations": { roles: ["admin", "purchasing", "inquiry_dept", "partner", "operations_head", "branch_oic"] },
    "/reports": { roles: ["admin", "branch_manager", "partner", "operations_head", "branch_oic"] },
    "/quotations": { perms: ["quotation.read"], roles: ["branch_manager"] },
    "/sales-orders": { perms: ["sales_order.read"], roles: ["branch_manager", "accounting", "logistics", "inquiry_dept"] },
    "/treasury/deposits": { roles: ["treasury"] },
    "/pick-and-pack": { perms: ["pickpack.read"], roles: ["admin", "inquiry_dept", "logistics"] },
    "/picklists": { perms: ["picklist.read"], roles: ["logistics"] },
    "/deliveries": { perms: ["delivery.read"], roles: ["logistics"] },
    "/returns": { perms: ["return.read"], roles: ["accounting", "logistics", "sales_agent", "branch_manager", "partner", "operations_head", "branch_oic", "inquiry_dept"] },
    "/approvals": { perms: ["approvals.read"], roles: ["branch_manager", "treasury", "accounting", "purchasing"] },

    "/customers": { perms: ["customer.read"] },
    "/products": { perms: ["product.read"] },

    // Agents get Leads, Pipeline, My Sales and My Activity — their own records only.
    // Everything else in CRM is for admin and owner, who both hold full CRM access.
    "/crm": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    "/crm/leads": { perms: ["crm.read"] },
    "/crm/pipeline": { perms: ["crm.read"] },
    "/crm/sales": { perms: ["crm.read"] },
    "/crm/accounts": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    "/crm/contacts": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    "/crm/activities": { perms: ["crm.read"] },
    "/crm/outreach": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    "/crm/team": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    "/crm/agent-stats": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    "/crm/analytics": { roles: ["admin", "owner"], perms: ["crm.reports"] },
    // Agents reach it too, but the SERVER scopes what they get: admin/owner every row,
    // a branch manager their branch, everyone else only their own.
    "/crm/quota": { roles: ["admin", "owner", "branch_manager", "sales_agent", "partner", "operations_head", "branch_oic"] },

    "/admin/users": { perms: ["users.read"] },
    "/admin/roles": { perms: ["roles.read"] },
    "/admin/schemas": { perms: ["schemas.read"] },
    "/admin/warehouses": { perms: ["schemas.read"] },
    "/admin/user-assignments": { perms: ["user_assignments.read"] },
    "/admin/approver-assignments": { perms: ["users.read"] },
    "/admin/price-groups": { perms: ["users.read"] },
    "/admin/email-setup": { perms: ["email_settings.read"] },
    "/admin/email-templates": { perms: ["email_templates.read"] },
    "/admin/sap-connection": { perms: ["sap_connections.read"] },
    "/admin/sync-monitor": { perms: ["sap_sync.read"] },
    "/admin/posting-queue": { perms: ["sap_posting_queue.read"] },
    "/admin/error-logs": { perms: ["sap_error_logs.read"] },
    "/admin/audit-logs": { perms: ["audit_logs.read"] },
    "/admin/crm/managers": { perms: ["user_assignments.read"] },
    // Saving the mapping needs schemas.write, so gate the page on the same permission
    // rather than showing owners a screen whose save button will 403.
    "/admin/crm/store-map": { perms: ["schemas.write"] },
    "/inquiry-requests": { roles: ["sales_agent", "branch_manager", "owner", "inquiry_dept", "purchasing"], perms: ["inquiry.read"] },
};

// Shared access check used by both the sidebar (canSee) and the route guard
// (RequireAccess in App.js). Admin always passes; otherwise the path's rule must
// grant it by role or permission. Unknown paths are denied for non-admins.
export function canAccessPath(path, user, hasPermission) {
    if (!user) return false;
    if (user.role === "admin") return true;
    // France 10-02: Treasury works deposit slips from /treasury/deposits, not the full Sales
    // Orders module — hide it even though the role carries sales_order.read.
    if (path === "/sales-orders" && user.role === "treasury") return false;
    // Orders (Non-Organic): only the owner (a Non-Organic user), the owner's approver,
    // or the Inquiry Department (admin already returned above).
    if (path === "/non-organic/orders") {
        return ["inquiry_dept", "purchasing"].includes(user.role)
            || user.business_mode === "non_organic" || !!user.is_non_organic_approver;
    }
    // Non-Organic users work only the PO-first order flow — hide the organic sales
    // pipeline (Quotations, Approvals) and the whole CRM (Leads, My Sales, Pipeline,
    // My Activity). Admin already returned true above, so this never affects admins.
    if (user.business_mode === "non_organic") {
        const HIDDEN_FOR_NON_ORGANIC = new Set([
            "/quotations", "/approvals",
            "/crm", "/crm/leads", "/crm/sales", "/crm/pipeline", "/crm/activities",
            "/crm/accounts", "/crm/contacts", "/crm/outreach", "/crm/team",
            "/crm/agent-stats", "/crm/analytics",
        ]);
        if (HIDDEN_FOR_NON_ORGANIC.has(path)) return false;
    }
    const rule = MODULE_ACCESS[path];
    if (!rule) return false;
    if ((rule.roles || []).includes(user.role)) return true;
    if ((rule.perms || []).some((perm) => hasPermission(perm))) return true;
    return false;
}

const NAV = [
    {
        section: "Workspace",
        items: [
            { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
            { to: "/inquiry", icon: Search, label: "Inquiry" },
            { to: "/inquiry-requests", icon: Clock, label: "Lead Time / ETA" },
            { to: "/admin/inquiry-reports", icon: BarChart3, label: "Inquiry Reports" },
            { to: "/cart", icon: ShoppingCart, label: "Sales Cart" },
            { to: "/non-organic/orders", icon: FileSignature, label: "Orders (Non-Organic)" },
            { to: "/project-registrations", icon: FileText, label: "Project Registrations" },
            { to: "/quotations", icon: FileText, label: "Quotations" },
            { to: "/sales-orders", icon: FileSignature, label: "Sales Orders" },
            { to: "/treasury/deposits", icon: Receipt, label: "Deposit Slips" },
            { to: "/pick-and-pack", icon: Boxes, label: "Pick & Pack" },
            { to: "/picklists", icon: ClipboardList, label: "Picklist" },
            { to: "/deliveries", icon: Truck, label: "Delivery" },
            { to: "/returns", icon: Undo2, label: "Returns" },
            { to: "/approvals", icon: Inbox, label: "Approvals" },
            { to: "/reports", icon: BarChart3, label: "Reports" },
        ],
    },
    {
        section: "Master Data",
        adminOnly: true,
        items: [
            { to: "/customers", icon: Users, label: "Customers" },
            { to: "/products", icon: Package, label: "Products" },
        ],
    },
    {
        section: "CRM",
        items: [
            { to: "/crm", icon: LayoutDashboard, label: "CRM Dashboard" },
            { to: "/crm/leads", icon: UserPlus, label: "Leads" },
            { to: "/crm/sales", icon: Receipt, label: "My Sales" },
            { to: "/crm/pipeline", icon: Workflow, label: "Pipeline" },
            { to: "/crm/accounts", icon: Building2, label: "Accounts" },
            { to: "/crm/contacts", icon: Contact, label: "Contacts" },
            { to: "/crm/activities", icon: ListTodo, label: "My Activity" },
            { to: "/crm/outreach", icon: Send, label: "Outreach" },
            { to: "/crm/team", icon: BarChart3, label: "Team Activity" },
            { to: "/crm/agent-stats", icon: BarChart3, label: "Agent Stats" },
            { to: "/crm/analytics", icon: PieChart, label: "Sales Analytics" },
            { to: "/crm/quota", icon: Target, label: "Sales Quota" },
        ],
    },
    {
        section: "Administration",
        adminOnly: true,
        items: [
            { to: "/admin/users", icon: Users, label: "Users" },
            { to: "/admin/roles", icon: Settings, label: "Roles & Permissions" },
            { to: "/admin/schemas", icon: Database, label: "Schema Management" },
            { to: "/admin/warehouses", icon: Database, label: "Warehouse Management" },
            { to: "/admin/user-assignments", icon: KeyRound, label: "User Assignments" },
            { to: "/admin/approver-assignments", icon: KeyRound, label: "Approver Assignments" },
            { to: "/admin/price-groups", icon: KeyRound, label: "Price Groups" },
            { to: "/admin/crm/managers", icon: KeyRound, label: "CRM Manager Assignments" },
            { to: "/admin/crm/store-map", icon: Database, label: "CRM Branch → SAP Company" },
            { to: "/admin/email-setup", icon: Mail, label: "Email Setup" },
            { to: "/admin/email-templates", icon: FileCode, label: "Email Templates" },
            { to: "/admin/sap-connection", icon: Plug, label: "SAP Connection" },
            { to: "/admin/sync-monitor", icon: RefreshCcw, label: "SAP Sync Monitor" },
            { to: "/admin/posting-queue", icon: ListChecks, label: "SAP Posting Queue" },
            { to: "/admin/error-logs", icon: AlertTriangle, label: "SAP Error Logs" },
            { to: "/admin/audit-logs", icon: ScrollText, label: "Audit Logs" },
        ],
    },
];

function ChangePasswordModal({ onClose }) {
    const [cur, setCur] = useState("");
    const [nw, setNw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (nw.length < 8) { setErr("New password must be at least 8 characters."); return; }
        if (nw !== confirm) { setErr("New password and confirmation do not match."); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: nw });
            setOk(true);
            setTimeout(onClose, 1200);
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-4">
                    <KeyRound className="w-4 h-4" />
                    <h3 className="font-semibold text-sm">Change password</h3>
                </div>
                {ok ? (
                    <div className="text-sm text-green-600 py-6 text-center">Password changed successfully.</div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <label className="text-xs text-muted-foreground">Current password</label>
                            <input type="password" autoComplete="current-password" className="sc-input mt-1 w-full" value={cur} onChange={(e) => setCur(e.target.value)} required />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">New password</label>
                            <input type="password" autoComplete="new-password" className="sc-input mt-1 w-full" value={nw} onChange={(e) => setNw(e.target.value)} required />
                            <div className="text-[11px] text-muted-foreground mt-1">At least 8 characters.</div>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Confirm new password</label>
                            <input type="password" autoComplete="new-password" className="sc-input mt-1 w-full" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                        </div>
                        {err && <div className="text-xs text-red-600">{err}</div>}
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary">Cancel</button>
                            <button type="submit" disabled={busy} className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50">{busy ? "Saving\u2026" : "Change password"}</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

// Only ACTION-required notifications reach the bell -- a user is alerted only when there is
// something for THEM to do (answer a request, approve, post arrived stock). FYI/awareness types
// (advance_po_notify, advance_po_approved, advanced_po_draft, credit_over_limit) do not.
const NOTIF_ACTION_TYPES = new Set(["inquiry_request_new", "inquiry_request_answered", "advance_po_approval", "advance_stock_arrived"]);

const NOTIF_ROUTE = {
    inquiry_request_new: "/inquiry-requests",
    inquiry_request_answered: "/inquiry-requests",
    advance_po_notify: "/non-organic/orders",
    advance_stock_arrived: "/non-organic/orders",
    non_organic_new: "/non-organic/orders",
};

function notifTimeAgo(iso) {
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch { return ""; }
}

function NotificationBell() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => {
        try {
            const { data } = await api.get("/notifications");
            const rows = (Array.isArray(data) ? data : (data?.notifications || [])).filter((n) => n.kind !== "email" && NOTIF_ACTION_TYPES.has(n.type));
            setItems(rows.slice(0, 30));
        } catch { /* ignore polling errors */ }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, []);
    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (!e.target.closest || !e.target.closest("[data-notif-root]")) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);
    const unread = items.filter((n) => n.read !== true).length;
    const openItem = async (n) => {
        try { if (n.read !== true) await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setOpen(false);
        const route = NOTIF_ROUTE[n.type];
        if (route) navigate(route);
    };
    const markAll = async () => {
        try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
        setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    };
    return (
        <div className="relative" data-notif-root>
            <button onClick={() => setOpen((v) => !v)} className="relative p-2 rounded-md hover:bg-secondary text-muted-foreground" title="Notifications" data-testid="notif-bell">
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notif-count">
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-lg border border-border bg-background shadow-lg z-50" data-testid="notif-panel">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                        <span className="text-sm font-semibold">Notifications</span>
                        {unread > 0 && <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="notif-mark-all">Mark all read</button>}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {items.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications.</div>}
                        {items.map((n) => (
                            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-3 py-2 border-b border-border/60 hover:bg-secondary/60 ${n.read !== true ? "bg-primary/5" : ""}`}>
                                <div className="flex items-start gap-2">
                                    {n.read !== true && <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 shrink-0" />}
                                    <div className="min-w-0">
                                        <div className={`text-sm truncate ${n.read !== true ? "font-semibold" : ""}`}>{n.title || "Notification"}</div>
                                        {n.message && <div className="text-xs text-muted-foreground break-words">{n.message}</div>}
                                        <div className="text-[10px] text-muted-foreground/70 mt-0.5">{notifTimeAgo(n.created_at)}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AppLayout() {
    const { user, logout, hasPermission } = useAuth();
    const nav = useNavigate();

    const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
    const [sapMode, setSapMode] = useState(null);
    const [pwOpen, setPwOpen] = useState(false);

    useEffect(() => {
        api.get("/health").then(({ data }) => setSapMode(data?.mode || null)).catch(() => setSapMode(null));
    }, []);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth < 768) {
                setCollapsed(true);
            }
        };

        window.addEventListener("resize", onResize);

        return () => window.removeEventListener("resize", onResize);
    }, []);

    const onLogout = async () => {
        await logout();
        nav("/login");
    };

    const canSee = (item) => canAccessPath(item.to, user, hasPermission);

    const visibleNav = NAV
        .filter((sec) => !sec.adminOnly || user?.role === "admin")
        .map((sec) => ({ ...sec, items: sec.items.filter(canSee) }))
        .filter((sec) => sec.items.length > 0);

    return (
        <div className="min-h-screen flex bg-background overflow-hidden">
            {/* Sidebar */}
            <aside
                className={`
                    ${collapsed ? "w-[58px]" : "w-[230px]"}
                    shrink-0
                    border-r
                    border-border
                    bg-[hsl(var(--sidebar))]
                    flex
                    flex-col
                    transition-all
                    duration-200
                `}
            >
                {/* Logo */}
                <div className="h-14 flex items-center justify-between px-2 border-b border-border">
                    <div className="flex items-center gap-2 overflow-hidden">
                        <div className="h-8 w-8 rounded bg-foreground text-background grid place-items-center text-xs font-bold shrink-0">
                            DK
                        </div>

                        {!collapsed && (
                            <div className="text-sm font-semibold whitespace-nowrap">
                                Sale Hub
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => setCollapsed((c) => !c)}
                        className="p-1 rounded hover:bg-secondary text-muted-foreground shrink-0"
                    >
                        {collapsed ? (
                            <ChevronRight className="w-4 h-4" />
                        ) : (
                            <ChevronLeft className="w-4 h-4" />
                        )}
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto py-2">
                    {visibleNav.map((sec) => (
                        <div key={sec.section} className="px-2 py-2">
                            {!collapsed && (
                                <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                                    {sec.section}
                                </div>
                            )}

                            <ul className="space-y-1">
                                {sec.items.map(({ to, icon: Icon, label }) => (
                                    <li key={to}>
                                        <NavLink
                                            to={to}
                                            className={({ isActive }) =>
                                                `
                                                flex
                                                items-center
                                                gap-3
                                                rounded-md
                                                transition-colors
                                                ${
                                                    collapsed
                                                        ? "justify-center px-0 py-3"
                                                        : "px-3 py-2"
                                                }
                                                ${
                                                    isActive
                                                        ? "bg-foreground text-background"
                                                        : "text-foreground/80 hover:bg-secondary"
                                                }
                                            `
                                            }
                                            title={label}
                                        >
                                            <Icon
                                                className="w-5 h-5 shrink-0"
                                                strokeWidth={1.9}
                                            />

                                            {!collapsed && (
                                                <span className="truncate text-sm">
                                                    {label}
                                                </span>
                                            )}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Footer */}
                <div className="border-t border-border p-2">
                    {!collapsed ? (
                        <div className="flex items-center justify-between gap-2">
                            <div className="overflow-hidden">
                                <div className="font-medium text-xs truncate">
                                    {user?.name}
                                </div>

                                <div className="text-[11px] text-muted-foreground truncate">
                                    {user?.role}
                                </div>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPwOpen(true)}
                                    title="Change password"
                                    className="p-2 rounded hover:bg-secondary text-muted-foreground"
                                >
                                    <KeyRound className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={onLogout}
                                    title="Log out"
                                    className="p-2 rounded hover:bg-secondary text-muted-foreground"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1">
                            <button
                                onClick={() => setPwOpen(true)}
                                title="Change password"
                                className="w-full flex justify-center py-2 text-muted-foreground rounded hover:bg-secondary"
                            >
                                <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                                onClick={onLogout}
                                title="Log out"
                                className="w-full flex justify-center py-2"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
                {/* Header */}
                <header
                    className="
                        border-b
                        border-border
                        bg-background
                        px-3 md:px-6
                        py-2
                        flex
                        flex-col
                        md:flex-row
                        md:items-center
                        md:justify-between
                        gap-2
                    "
                >
                    <div className="flex items-center gap-2 flex-wrap text-sm min-w-0">
                        <Boxes className="w-4 h-4 text-muted-foreground shrink-0" />

                        <span className="text-muted-foreground hidden sm:inline">
                            DK Homestyle
                        </span>

                        <span className="text-muted-foreground/60 hidden sm:inline">
                            /
                        </span>

                        <span className="font-medium">
                            Sale Hub
                        </span>

                        {sapMode && (
                            <span className={`sc-pill whitespace-nowrap border ${sapMode === "REAL"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                SAP MODE: {sapMode}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <NotificationBell />
                        <div className="text-xs text-muted-foreground truncate max-w-[220px] hidden md:block">
                            {user?.email}
                        </div>
                    </div>
                </header>

                {/* Content */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    <div className="w-full max-w-full">
                        <Outlet />
                    </div>
                </div>
            </main>
        </div>
    );
}
